// eslint-disable @typescript-eslint/consistent-type-assertions
import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { Metamodel } from 'app/model/base/model';
import { AzureFunctionLinkedService } from 'app/model/linkedservice/azure-function.linkedservice';
import { format, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { azureResourceSelectMethodOptions, authenticationReferenceMethodOptions } from '../consts/metadata-consts';
import { AuthenticationType } from 'app/interfaces/IAuthenticationType';
import { CredentialTypeOptions } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';
import { ControlMetadata } from '../../extensibility/metadatas/control/control-metadata';
import { azureDefaultAuthRefTypes } from '../utls/authenticationUtils';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';

const miResourceId: ControlMetadata = {
    type: 'textbox',
    bind: 'resourceId',
    isLabelVertical: true,
    label: stringResources.ResourceIDLabel,
    description: stringResources.AzureFunctionLinkedService_ResourceIdTooltip,
    required: false
};

const dynamicResourceId: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: miResourceId
    }
];

const supportMiWithCreds: ControlMetadata = {
    type: 'radio-switch',
    nested: 1,
    label: stringResources.Authentication_Reference_Method_Label,
    description: stringResources.Authentication_Reference_Method_Description,
    options: authenticationReferenceMethodOptions,
    isLabelVertical: true,
    defaultValue: '{isAuthRefMethodCredential ? "credential" : "inline"}',
    cases: [
        {
            caseValue: azureDefaultAuthRefTypes.InlineRefTypeAuthentication,
            children: [
                // system Assigned Managed Identity
                {
                    type: 'management-identity-info',
                    resourceType: stringResources.AzureFunctionActivityLabel
                },
                ...dynamicResourceId
            ]
        },
        {
            caseValue: azureDefaultAuthRefTypes.CredentialRefTypeAuthentication,
            children: [
                {
                    type: 'credential-select',
                    bind: 'credential',
                    filterCredentialType: CredentialTypeOptions.ManagedIdentity,
                    required: true,
                    validators: [
                        { type: 'required', errorMessage: '' }
                    ]
                },
                ...dynamicResourceId
            ]
        }
    ]
};

const supportMiWithoutCreds: ControlMetadata = {
    // system Assigned Managed Identity
    type: 'management-identity-info',
    resourceType: stringResources.AzureFunctionActivityLabel
};

const functionAppUrl: ControlMetadata = {
    type: 'textbox',
    bind: 'functionAppUrl',
    required: true,
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    warningMessageCalculator: (value: string) =>
        stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning,
    label: stringResources.ModelName_FunctionAppUrl,
    isLabelVertical: true,
    placeholder: 'https://<accountName>.azurewebsites.net',
    description: stringResources.AzureFunction_FunctionAppUrl_tooltip
};

const dynamicFunctionAppUrl: ControlMetadata = {
    type: 'parameterizable',
    child: functionAppUrl,
    warningMessageCalculator: (value: string) =>
        stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning
};

const accountName: ControlMetadata = {
    type: 'radio-switch',
    bind: 'accountNameSelectionMethod',
    nested: 1,
    label: stringResources.AzureFunction_AccountName_SelectionMethod,
    description: stringResources.AzureFunction_AccountName_SelectionMethod_Description,
    options: azureResourceSelectMethodOptions,
    isLabelVertical: true,
    defaultValue: '{functionAppUrl != null ? "manual" : "azure"}',
    cases: [
        {
            caseValue: 'azure',
            children: [
                {
                    type: 'azure-function-select',
                    bind: {
                        functionAppUrl: 'functionAppUrl',
                        resourceId: 'privateLinkResourceId'
                    },
                    validators: [
                        { type: 'required' }
                    ]
                }
            ]
        },
        {
            caseValue: 'manual',
            children: [dynamicFunctionAppUrl]
        }
    ]
};

const dynamicFunctionKey: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    enableAKVParameterization: true,
    bind: 'functionKey',
    label: stringResources.ModelName_FunctionKey,
    child: {
        type: 'parameterizable',
        attribute: 'functionKey',
        disableAddParameter: true,
        child: {
            type: 'password',
            label: stringResources.ModelName_FunctionKey,
            required: false,
            isLabelVertical: true,
            description: stringResources.AzureFunction_FunctionAppKey_tooltip
        }
    },
    validators: [
    ],
    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureFunctionActivityLabel),
        stringResources.ModelName_FunctionKey)
};

const servicePrincipalControls: ControlMetadata[] = [
    {
        type: 'tenant-textbox',
        bind: 'tenant',
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true,
        label: stringResources.ModelName_Tenant,
        required: true
    },
    {
        type: 'textbox',
        bind: 'servicePrincipalId',
        label: stringResources.ModelName_ServicePrincipalID,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true,
        description: '<a target="_blank" href="https://learn.microsoft.com/en-us/azure/data-factory/control-flow-azure-function-activity">'
            + stringResources.LearnMore + '</a>',
        escapeDescription: false
    },
    {
        type: 'dynamic-akv-switcher',
        bind: 'servicePrincipalKey',
        label: stringResources.ModelName_ServicePrincipalKey,
        child: {
            type: 'password',
            label: stringResources.ModelName_ServicePrincipalKey,
            required: true,

            isLabelVertical: true
        },
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureFunctionActivityLabel),
            stringResources.ModelName_ServicePrincipalKey)
    }
];

const authenticationControls: ControlMetadata = {
    type: 'select-switch',
    required: true,
    isLabelVertical: true,
    bind: 'authentication',
    label: stringResources.Authentication_Method_Label,
    options: [
        {
            label: stringResources.AuthenticationType_ManagedServiceIdentity,
            value: AuthenticationType.ManagedServiceIdentity
        },
        {
            label: stringResources.Anonymous_DisplayText,
            value: AuthenticationType.Anonymous
        },
        {
            label: stringResources.AuthenticationType_ServicePrincipal,
            value: AuthenticationType.ServicePrincipal,
            hidden: !GlobalVariables.enableSpnAuthInAzFuncActivity
        },
        {
            label: stringResources.AuthenticationType_UserAssignedManagedIdentity,
            value: AuthenticationType.UserAssignedManagedIdentity,
            hidden: !GlobalVariables.enableUamiAuthInAzFuncActivity
        }
    ],
    defaultValue: AuthenticationType.Anonymous,
    cases: [
        {
            caseValue: AuthenticationType.ManagedServiceIdentity,
            children: [
                ...(GlobalVariables.isSamiSupportEnabled ? [supportMiWithCreds] : [supportMiWithoutCreds, ...dynamicResourceId]),
                ...(!GlobalVariables.updateAzFuncSAMI ? [dynamicFunctionKey] : [])
            ]
        },
        {
            caseValue: AuthenticationType.UserAssignedManagedIdentity,
            children: [...(GlobalVariables.enableUamiAuthInAzFuncActivity ? [
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                {
                    type: 'credential-select',
                    bind: 'credential',
                    filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                    required: true,
                    validators: [
                        { type: 'required', errorMessage: '' }
                    ]
                } as ControlMetadata, ...dynamicResourceId, dynamicFunctionKey] : [])
            ]
        },
        {
            caseValue: AuthenticationType.Anonymous,
            children: [dynamicFunctionKey]
        },
        {
            caseValue: AuthenticationType.ServicePrincipal,
            children: [...servicePrincipalControls, ...dynamicResourceId, dynamicFunctionKey]
        },
    ]
};

export const azureFunctionMetadata: ILinkedServiceTypeMetadata = {
    type: Metamodel.modelFor(AzureFunctionLinkedService).name,
    displayName: stringResources.AzureFunctionActivityLabel,
    supportedInTrident: true,
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.Extension,
        resourceKind: 'AzureFunction' as ResourceKind
    },
    supportOPDG: true,
    isKnownInPipelineContext: true,
    icon: {
        className: 'svg-i-100x100-AzureFunction'
    },
    category: ['Azure'],
    isCompute: true,
    isFirstClass: true,
    copySinkTypes: [],
    copySourceTypes: [],
    disableQueryAPI: true,
    supportFirstClassParameterExperience: true,
    controls: [accountName, authenticationControls]
};
